import { t } from '@/utils/i18n'
import { Container } from '@/components/ui/Container'
import { applicationCategories } from '@/data/applicationCases'

export function IndustriesSection(): HTMLElement {
  const section = document.createElement('section')
  section.className = 'section-container bg-slate-50'

  const container = Container({
    children: [
      createSectionHeader(),
      createApplicationsCarousel(),
    ],
  })

  section.appendChild(container)
  return section
}

function createSectionHeader(): HTMLElement {
  const header = document.createElement('div')
  header.className = 'text-center mb-12'

  const title = document.createElement('h2')
  title.textContent = t('industries.sectionTitle')
  title.className = 'text-4xl sm:text-5xl font-bold text-slate-900 mb-4'

  const subtitle = document.createElement('p')
  subtitle.textContent = t('industries.sectionSubtitle')
  subtitle.className = 'text-xl text-slate-600'

  header.appendChild(title)
  header.appendChild(subtitle)
  return header
}

// Map categories to display data with images and i18n keys
interface CategoryDisplayConfig {
  imagePath: string
  tempKey: string
  applicationKey: string
  benefitKey: string
}

const categoryDisplayConfigs: Record<string, CategoryDisplayConfig> = {
  industrial: {
    imagePath: '/images/aluminum_smelting.webp',
    tempKey: 'industriesSection.industrial.temp',
    applicationKey: 'industriesSection.industrial.application',
    benefitKey: 'industriesSection.industrial.benefit'
  },
  residential: {
    imagePath: '/images/district_heating.webp',
    tempKey: 'industriesSection.residential.temp',
    applicationKey: 'industriesSection.residential.application',
    benefitKey: 'industriesSection.residential.benefit'
  },
  grid: {
    imagePath: '/images/data_centers.webp',
    tempKey: 'industriesSection.grid.temp',
    applicationKey: 'industriesSection.grid.application',
    benefitKey: 'industriesSection.grid.benefit'
  },
  smr: {
    imagePath: '/images/smr.webp',
    tempKey: 'industriesSection.smr.temp',
    applicationKey: 'industriesSection.smr.application',
    benefitKey: 'industriesSection.smr.benefit'
  }
}

function getCategoryDisplayData(slug: string): { imagePath: string; temp: string; application: string; benefit: string } | null {
  const config = categoryDisplayConfigs[slug]
  if (!config) return null
  return {
    imagePath: config.imagePath,
    temp: t(config.tempKey),
    application: t(config.applicationKey),
    benefit: t(config.benefitKey)
  }
}

function createApplicationsCarousel(): HTMLElement {
  const wrapper = document.createElement('div')
  wrapper.className = 'mb-12'

  // Carousel container
  const carousel = document.createElement('div')
  carousel.className = 'overflow-x-auto scrollbar-hide scroll-smooth mb-4 py-4'
  carousel.style.scrollbarWidth = 'none' // Firefox
  ;(carousel.style as any).msOverflowStyle = 'none' // IE/Edge

  // Carousel inner
  const carouselInner = document.createElement('div')
  carouselInner.className = 'flex gap-6 px-4'

  applicationCategories.forEach((category) => {
    const displayData = getCategoryDisplayData(category.slug)
    if (!displayData) return

    const card = document.createElement('a')
    card.href = `/applications/${category.slug}`
    card.className = 'flex-shrink-0 w-80 h-[427px] p-0 card overflow-hidden cursor-pointer transition-transform hover:scale-105 flex flex-col'
    card.addEventListener('click', (e) => {
      e.preventDefault()
      window.dispatchEvent(new CustomEvent('navigate', { detail: `/applications/${category.slug}` }))
    })

    // Image section (taller - roughly 240px)
    const imageSection = document.createElement('div')
    imageSection.className = 'w-full h-[240px] overflow-hidden bg-slate-100'
    const image = document.createElement('img')
    image.src = displayData.imagePath
    image.alt = category.title
    image.className = 'w-full h-full object-cover'
    imageSection.appendChild(image)

    // Content section (compact - roughly 187px)
    const contentSection = document.createElement('div')
    contentSection.className = 'flex-1 flex flex-col p-6'

    // Title row
    const titleRow = document.createElement('div')
    titleRow.className = 'flex items-center gap-2 mb-2'

    const title = document.createElement('h3')
    title.textContent = category.title
    title.className = 'text-base font-bold text-slate-900'

    titleRow.appendChild(title)

    // Case count badge
    const caseCount = document.createElement('span')
    caseCount.textContent = `${category.cases.length} ${t('industriesSection.cases')}`
    caseCount.className = 'ml-auto text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-medium'
    titleRow.appendChild(caseCount)

    // Structured description (compact)
    const descriptionWrapper = document.createElement('div')
    descriptionWrapper.className = 'flex-1 space-y-1 mb-2'

    // Temperature
    const tempRow = document.createElement('div')
    tempRow.className = 'flex items-baseline gap-2'
    const tempLabel = document.createElement('span')
    tempLabel.textContent = t('industriesSection.tempLabel')
    tempLabel.className = 'text-[10px] font-bold text-slate-500 tracking-wider min-w-[45px]'
    const tempValue = document.createElement('span')
    tempValue.textContent = displayData.temp
    tempValue.className = 'text-xs text-slate-900 font-semibold'
    tempRow.appendChild(tempLabel)
    tempRow.appendChild(tempValue)

    // Application
    const appRow = document.createElement('div')
    appRow.className = 'flex items-baseline gap-2'
    const appLabel = document.createElement('span')
    appLabel.textContent = t('industriesSection.useLabel')
    appLabel.className = 'text-[10px] font-bold text-slate-500 tracking-wider min-w-[45px]'
    const appValue = document.createElement('span')
    appValue.textContent = displayData.application
    appValue.className = 'text-xs text-slate-700'
    appRow.appendChild(appLabel)
    appRow.appendChild(appValue)

    // Benefit
    const benefitRow = document.createElement('div')
    benefitRow.className = 'text-slate-600 text-[11px] leading-snug mt-1.5 pt-1.5 border-t border-slate-200'
    benefitRow.textContent = displayData.benefit

    descriptionWrapper.appendChild(tempRow)
    descriptionWrapper.appendChild(appRow)
    descriptionWrapper.appendChild(benefitRow)

    // Explore button (right-aligned at bottom)
    const exploreBtn = document.createElement('div')
    exploreBtn.className = 'flex items-center justify-end gap-1 text-orange-600 font-semibold text-[13px] group/btn'

    const btnText = document.createElement('span')
    btnText.textContent = t('industriesSection.explore')

    const btnArrow = document.createElement('span')
    btnArrow.className = 'transition-transform group-hover/btn:translate-x-1'
    btnArrow.innerHTML = '<svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>'

    exploreBtn.appendChild(btnText)
    exploreBtn.appendChild(btnArrow)

    contentSection.appendChild(titleRow)
    contentSection.appendChild(descriptionWrapper)
    contentSection.appendChild(exploreBtn)

    card.appendChild(imageSection)
    card.appendChild(contentSection)

    carouselInner.appendChild(card)
  })

  // Add CTA as last carousel item - transparent with arrow only
  const ctaSlide = document.createElement('a')
  ctaSlide.href = '/applications'
  ctaSlide.className = 'flex-shrink-0 w-32 flex flex-col items-center justify-center gap-4 bg-transparent hover:bg-gradient-to-br hover:from-gray-100 hover:to-gray-200 rounded-xl transition-all duration-300 cursor-pointer group'
  ctaSlide.style.minHeight = '400px'
  ctaSlide.addEventListener('click', (e) => {
    e.preventDefault()
    window.dispatchEvent(new CustomEvent('navigate', { detail: '/applications' }))
  })

  // Arrow icon only
  const arrow = document.createElement('div')
  arrow.className = 'text-slate-900 group-hover:text-orange-600 transition-all duration-300'
  arrow.innerHTML = '<svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>'

  // Vertical text
  const textContainer = document.createElement('div')
  textContainer.className = 'flex flex-col items-center gap-1'

  const exploreText = document.createElement('span')
  exploreText.textContent = t('industriesSection.viewAll1')
  exploreText.className = 'text-sm font-semibold text-slate-900 group-hover:text-orange-600 tracking-wide transition-colors duration-300'

  const allText = document.createElement('span')
  allText.textContent = t('industriesSection.viewAll2')
  allText.className = 'text-sm font-semibold text-slate-900 group-hover:text-orange-600 tracking-wide transition-colors duration-300'

  textContainer.appendChild(exploreText)
  textContainer.appendChild(allText)

  ctaSlide.appendChild(arrow)
  ctaSlide.appendChild(textContainer)

  carouselInner.appendChild(ctaSlide)

  carousel.appendChild(carouselInner)

  // Navigation buttons container - positioned at bottom right
  const navContainer = document.createElement('div')
  navContainer.className = 'flex justify-end gap-2 px-4'

  const prevButton = document.createElement('button')
  prevButton.className = 'w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center text-slate-700 hover:bg-orange-50 hover:text-orange-600 transition-colors'
  prevButton.innerHTML = '<svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/></svg>'
  prevButton.addEventListener('click', () => {
    carousel.scrollBy({ left: -320, behavior: 'smooth' })
  })

  const nextButton = document.createElement('button')
  nextButton.className = 'w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center text-slate-700 hover:bg-orange-50 hover:text-orange-600 transition-colors'
  nextButton.innerHTML = '<svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>'
  nextButton.addEventListener('click', () => {
    carousel.scrollBy({ left: 320, behavior: 'smooth' })
  })

  navContainer.appendChild(prevButton)
  navContainer.appendChild(nextButton)

  wrapper.appendChild(carousel)
  wrapper.appendChild(navContainer)

  return wrapper
}
